import ActivePotions from './laboratory/ActivePotions';
import { Component } from 'preact';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';
import CardInspector, { RARITY_LABEL_COLOR } from './CardInspector';
import CardOwnedBadge from './CardOwnedBadge';
import { isFreePlay } from '../utils/freePlay';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import PackRevealCard from './PackRevealCard';
import { BackButton } from './ui/navButtons';
import PackOpeningFX from './PackOpeningFX';
import AmbientParticles from './AmbientParticles';
import StoreTorchFX from './StoreTorchFX';
import CandleFlames from './CandleFlames';
import CoverArtCanvas from './CoverArtCanvas';
import { STORE_DEFAULT_CANDLES } from './ArenaStore';
import BoosterPackScene from './play/BoosterPackScene';
import { getLocalApiOrigin } from '../utils/localApi';
import { isFoilFinish, FOIL_OVERLAY_CLASSES } from '../utils/sorcery/foil.js';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, PANEL_BG,
  BEVELED_BTN, GOLD_BTN, RADIUS,
} from '../lib/medievalTheme';
import { playUI, UI } from '../utils/arena/uiSounds';
import { t } from '../localization/t';
import {
  finalizePack,
  SEALED_BOXES,
  isSealedBoxKey,
  isSitesBoosterKey,
} from '../utils/arena/packsApi';
import { ProductPreview, hasProductPreview } from './ProductPreview';
import { buildOwnedMap } from '../utils/arena/collectionUtils';
import { resolvePackContents } from '../utils/arena/packsApi';
import { getSkillState } from '../utils/arena/skills';
import { sightCostFor, rerollPackCard } from '../utils/arena/oracle';
import { potionOutcome, potionArt, potionArtMask } from '../utils/arena/alchemist';
import { getMaxCopies } from '../utils/sorcery/deckRules';

// The 3D renders are all framed by the same camera, so they need no per-set
// correction. The old table existed because each set's flat catalogue art sat
// in a different amount of margin.
const BOOSTER_SCALE = { gothic: 1, arthurian: 1, beta: 1 };

/**
 * Boosters with a pre-rendered 3D pack, from `scripts/render-pack-thumbs.js`.
 *
 * The picker showed flat catalogue art while the opener showed a lit model, so
 * the pack you clicked and the pack that burst were visibly different objects.
 * These renders come out of the same PackScene, so the two views cannot drift.
 * A set missing from this list falls back to its flat art rather than 404ing.
 */
const PACK_3D_SETS = new Set(['gothic', 'arthurian', 'beta']);

/** The store artwork's aspect. Shared with the store so candles line up. */
const STORE_ART_ASPECT = 1672 / 941;

/** How long the 3D pack gets to load before we open the pack without it. */
const SCENE_READY_MS = 12000;
/** How long the burst gets, once the player has committed. Runs in ~0.75s. */
const SCENE_BURST_MS = 5000;

// Forged-gold-plate button vocabulary. Mirrors the End Turn button on
// the game board so the summary-screen CTA reads with the same depth,
// dimension, and texture as the most important in-game button.
const FORGED_GOLD_BLEND = 'multiply, multiply, normal, overlay, overlay, overlay, overlay, normal, normal';
const FORGED_GOLD_BG_IDLE = [
  'url("/tex-noise-panel.webp")',
  'url("/tex-noise-panel.webp")',
  'linear-gradient(180deg, rgba(255, 245, 215, 0.35) 0%, rgba(255, 240, 200, 0.10) 30%, transparent 55%, rgba(60, 35, 10, 0.22) 82%, rgba(60, 35, 10, 0.42) 100%)',
  GOLD_BTN.background,
].join(', ');
const FORGED_GOLD_BG_HOVER = [
  'url("/tex-noise-panel.webp")',
  'url("/tex-noise-panel.webp")',
  'linear-gradient(180deg, rgba(255, 250, 225, 0.48) 0%, rgba(255, 245, 210, 0.16) 30%, transparent 55%, rgba(95, 55, 12, 0.22) 82%, rgba(95, 55, 12, 0.40) 100%)',
  GOLD_BTN.background,
].join(', ');
const FORGED_GOLD_BG_PRESS = [
  'url("/tex-noise-panel.webp")',
  'url("/tex-noise-panel.webp")',
  'linear-gradient(180deg, rgba(60, 35, 10, 0.28) 0%, rgba(60, 35, 10, 0.12) 35%, transparent 60%, rgba(255, 240, 200, 0.10) 100%)',
  GOLD_BTN.background,
].join(', ');
const FORGED_GOLD_SHADOW_IDLE = [
  'inset 0 1px 0 rgba(255, 240, 200, 0.45)',
  'inset 0 -1px 0 rgba(60, 40, 10, 0.35)',
  'inset 0 0 22px rgba(120, 80, 20, 0.22)',
  '0 0 0 1px rgba(232, 195, 110, 0.75)',
  '0 0 0 3px rgba(0, 0, 0, 0.55)',
  '0 0 0 4px rgba(180, 140, 60, 0.28)',
  '0 6px 16px rgba(0, 0, 0, 0.55)',
].join(', ');
const FORGED_GOLD_SHADOW_HOVER = [
  'inset 0 1px 0 rgba(255, 245, 215, 0.6)',
  'inset 0 -1px 0 rgba(60, 40, 10, 0.35)',
  'inset 0 0 26px rgba(160, 110, 35, 0.32)',
  '0 0 0 1px rgba(255, 220, 130, 0.9)',
  '0 0 0 3px rgba(0, 0, 0, 0.55)',
  '0 0 0 4px rgba(232, 195, 110, 0.5)',
  '0 8px 22px rgba(0, 0, 0, 0.6), 0 0 26px rgba(232, 195, 110, 0.28)',
].join(', ');
const FORGED_GOLD_SHADOW_PRESS = [
  'inset 0 2px 4px rgba(60, 40, 10, 0.50)',
  'inset 0 -1px 0 rgba(255, 240, 200, 0.18)',
  'inset 0 0 30px rgba(120, 80, 20, 0.38)',
  '0 0 0 1px rgba(212, 168, 67, 0.7)',
  '0 0 0 3px rgba(0, 0, 0, 0.6)',
  '0 0 0 4px rgba(180, 140, 60, 0.22)',
  '0 2px 6px rgba(0, 0, 0, 0.55)',
].join(', ');
const FORGED_GOLD_TEXT_SHADOW = '0 1px 0 rgba(255, 240, 200, 0.5), 0 -1px 0 rgba(60, 40, 10, 0.4)';
const FORGED_GOLD_STYLE_IDLE = {
  ...GOLD_BTN,
  background: FORGED_GOLD_BG_IDLE,
  backgroundBlendMode: FORGED_GOLD_BLEND,
  border: 'none',
  borderRadius: 'var(--radius-xl)',
  boxShadow: FORGED_GOLD_SHADOW_IDLE,
  textShadow: FORGED_GOLD_TEXT_SHADOW,
  transition: 'transform 80ms ease-out, box-shadow 80ms ease-out, background 80ms ease-out',
  transform: 'scale(1)',
};
const FORGED_GOLD_HANDLERS = {
  onMouseEnter: (e) => {
    e.currentTarget.style.background = FORGED_GOLD_BG_HOVER;
    e.currentTarget.style.boxShadow = FORGED_GOLD_SHADOW_HOVER;
    e.currentTarget.style.transform = 'scale(1.015)';
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.background = FORGED_GOLD_BG_IDLE;
    e.currentTarget.style.boxShadow = FORGED_GOLD_SHADOW_IDLE;
    e.currentTarget.style.transform = 'scale(1)';
  },
  onMouseDown: (e) => {
    e.currentTarget.style.background = FORGED_GOLD_BG_PRESS;
    e.currentTarget.style.boxShadow = FORGED_GOLD_SHADOW_PRESS;
    e.currentTarget.style.transform = 'scale(0.985)';
  },
  onMouseUp: (e) => {
    e.currentTarget.style.background = FORGED_GOLD_BG_HOVER;
    e.currentTarget.style.boxShadow = FORGED_GOLD_SHADOW_HOVER;
    e.currentTarget.style.transform = 'scale(1.015)';
  },
};

// Boxes don't need a per-set scale. They render at their natural
// container size and aren't hand-tuned per artwork like boosters are.
function getProductScale(setKey) {
  if (isSealedBoxKey(setKey)) return 1;
  return BOOSTER_SCALE[setKey] || 1;
}

function getProductImage(setKey) {
  const base = getLocalApiOrigin();
  if (PACK_3D_SETS.has(setKey)) return `${base}/game-assets/booster-${setKey}-3d.webp`;
  const prefix = isSealedBoxKey(setKey) ? 'box' : 'booster';
  return `${base}/game-assets/${prefix}-${setKey}.webp`;
}

// Kept as an alias so existing call sites don't churn while we
// migrate. New code should call getProductImage directly.
const getBoosterImage = getProductImage;

function playSound(src, volume = 0.5) {
  playUI(src, { volume });
}

// Rarity reads by contrast. The previous table gave every card a glow,
// Ordinary included, so a Unique had nothing to stand out against. Which is
// why players reported not noticing it. Commons now carry none at all, and the
// rare tiers move: in a grid of fifteen static tiles, motion is what the eye
// goes to.
const RARITY_GLOW = {
  Ordinary: 'none',
  Exceptional: '0 0 14px rgba(59,130,246,0.35), 0 0 30px rgba(59,130,246,0.12)',
  Elite: '0 0 26px rgba(168,85,247,0.7), 0 0 60px rgba(168,85,247,0.3)',
  Unique: '0 0 34px rgba(245,158,11,0.85), 0 0 78px rgba(245,158,11,0.42), 0 0 130px rgba(245,158,11,0.18)',
  Avatar: '0 0 40px rgba(220,40,40,0.85), 0 0 90px rgba(220,40,40,0.45), 0 0 150px rgba(245,158,11,0.22)',
};

const RARITY_GLOW_HOVER = {
  Ordinary: '0 0 14px rgba(255,255,255,0.14)',
  Exceptional: '0 0 24px rgba(59,130,246,0.5), 0 0 50px rgba(59,130,246,0.2)',
  Elite: '0 0 38px rgba(168,85,247,0.85), 0 0 84px rgba(168,85,247,0.4)',
  Unique: '0 0 48px rgba(245,158,11,1), 0 0 110px rgba(245,158,11,0.55), 0 0 170px rgba(245,158,11,0.25)',
  Avatar: '0 0 56px rgba(220,40,40,1), 0 0 120px rgba(220,40,40,0.55), 0 0 180px rgba(245,158,11,0.3)',
};

/** Which tiers breathe. */
const RARITY_BREATHES = new Set(['Elite', 'Unique', 'Avatar']);

/**
 * A card the player has to turn over themselves.
 *
 * Foils and Uniques only. Making every card face down would turn the reveal
 * into fifteen chores; making none of them face down is what the screen does
 * today, where the best card in the pack arrives with exactly the same
 * ceremony as the worst.
 */
function startsFaceDown(entry) {
  const rarity = entry?.slotRarity || entry?.rarity;
  return isFoilFinish(entry?.printing?.foiling) || rarity === 'Unique' || rarity === 'Avatar';
}

/** Sites carry the atlas back; everything else the spellbook. */
function cardBackFor(entry) {
  const isSite = entry?.card?.type === 'Site' || entry?.card?.played_horizontally;
  return `${getLocalApiOrigin()}/game-assets/${isSite ? 'cardback-atlas-rounded.png' : 'cardback-spellbook-rounded.png'}`;
}

const RARITY_BORDER_COLOR = {
  Ordinary: 'rgba(255,255,255,0.1)',
  Exceptional: 'rgba(59,130,246,0.5)',
  Elite: 'rgba(168,85,247,0.5)',
  Unique: 'rgba(245,158,11,0.6)',
  Avatar: 'rgba(220,40,40,0.6)',
};

export default class ArenaPackOpening extends Component {
  constructor(props) {
    super(props);
    this.state = {
      phase: 'sealed',
      shaking: false,
      hoveredIndex: -1,
      inspectedEntry: null,
      packCards: props.pack?.cards ? props.pack.cards.slice() : [],
      // Indices the player has turned over, and the one that flipped most
      // recently so its flash fires exactly once.
      revealed: new Set(),
      flashIndex: -1,
      // Oracle Sight. `flags.rerolledRarities` mirrors the server's per-pack
      // usage; `rerollRarity` is the armed rarity whose next card click
      // re-rolls; `mutating` blocks a double-click from paying twice.
      flags: { rerolledRarities: [], ...(props.pack?.flags || {}) },
      rerollRarity: null,
      mutating: false,
      mutationError: null,
    };
    // Single-shot guards. `_packCommitted` flips the moment the
    // player clicks to open the pack. HandlePackClick fires the
    // server /open call exactly once. `_packFinalized` flips when an
    // exit handler runs and deletes the pending_packs row.
    this._packCommitted = false;
    this._packFinalized = false;
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    if (this.props.autoOpen) {
      // Skip the sealed chooser. Go straight to opening animation
      this.handlePackClick();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    removeDismiss(this._escDismissId);
    this.clearSceneWatchdog();
  }

  // The cards are granted the instant the player commits, before anything is
  // drawn. So every path out of the opening animation has to end at the
  // reveal. On the burst path the only two exits are the scene's own
  // `onCardsOut` and its `onUnavailable`, and a scene that hangs (assets that
  // never resolve, a lost GL context, a dead render loop) reports neither: the
  // player is left staring at an empty room, and the pack they paid for is
  // already spent. These deadlines are the backstop. If the scene owes us a
  // milestone and doesn't deliver, fall back to the flat opening and show the
  // cards. Waiting on the player's own click is not a deadline and is not
  // watched.
  armSceneWatchdog = (ms) => {
    this.clearSceneWatchdog();
    this._sceneWatchdog = setTimeout(() => {
      this._sceneWatchdog = null;
      this.handleSceneUnavailable('the pack scene stalled');
    }, ms);
  };

  clearSceneWatchdog = () => {
    if (this._sceneWatchdog) clearTimeout(this._sceneWatchdog);
    this._sceneWatchdog = null;
  };

  componentDidUpdate(prevProps, prevState) {
    // The open response may carry different contents than the pending
    // listing did (the Alchemist's pack potions act at open): follow the
    // parent's pack when it is the same pack with new cards or flags.
    const pack = this.props.pack;
    if (pack && pack !== prevProps.pack && pack.id && pack.id === prevProps.pack?.id
      && (pack.cards !== prevProps.pack.cards || pack.flags !== prevProps.pack.flags)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        packCards: pack.cards ? pack.cards.slice() : this.state.packCards,
        flags: { rerolledRarities: [], ...(pack.flags || {}) },
      });
    }
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedEntry &&
      this.state.phase === 'summary' &&
      this.state.hoveredIndex >= 0 &&
      this.state.hoveredIndex !== prevState.hoveredIndex &&
      this.inspectorHold.consumePending()
    ) {
      const entry = this.state.packCards[this.state.hoveredIndex];
      if (entry) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ inspectedEntry: entry });
      }
    }

    const inspectorOpen = this.state.inspectedEntry != null;
    const wasInspectorOpen = prevState.inspectedEntry != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => this.setState({ inspectedEntry: null }));
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  handleKeyDown = (e) => {
    if (e.repeat) return; // prevent key repeat flicker
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      if (this.state.inspectedEntry) {
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
      } else if (this.state.hoveredIndex >= 0 && this.state.phase === 'summary') {
        this.inspectorHold.noteOpen();
        this.setState({ inspectedEntry: this.state.packCards[this.state.hoveredIndex] });
      } else if (this.state.phase === 'summary') {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    this.setState({ inspectedEntry: null });
  };

  // Only a plain booster gets the burst. A sealed box is a different object
  // with a different artwork, and the Sites preview is a composite of two
  // images rather than a single pack that could be torn.
  usesBurst() {
    const setKey = this.props.pack?.setKey;
    return Boolean(setKey) && !isSealedBoxKey(setKey) && !isSitesBoosterKey(setKey);
  }

  // Both opening paths land here. The flat one on a fixed timer, the tear
  // when the player has actually opened the pack.
  enterSummary = () => {
    this.clearSceneWatchdog();
    if (this.state.phase === 'summary') return;
    playSound('snd-card-slide-1.ogg', 0.4);
    this.setState({ phase: 'summary', shaking: false, entryDone: false });
    const cardCount = this.state.packCards?.length || 15;
    setTimeout(() => this.setState({ entryDone: true }), cardCount * 50 + 400);
  };

  // The 3D pack could not run. No WebGL, or its model failed to load. Drop
  // back to the flat opening. A pack that cannot be rendered must still look
  // like it was opened; going straight to the cards reads as the animation
  // simply not existing.
  handleSceneUnavailable = (reason) => {
    if (this.state.sceneFailed) return;
    this.clearSceneWatchdog();
    if (reason) console.error(`[ArenaPackOpening] falling back to the flat opening: ${reason}`);
    this.setState({ sceneFailed: true, shaking: true });
    playSound('snd-pack-opening.mp3', 0.6);
    setTimeout(this.enterSummary, 900);
  };

  handlePackClick = () => {
    if (this.state.phase !== 'sealed') return;
    // Commit the pack on click. The server stamps opened_at, grants
    // the cards, and removes the pack from the player's pending list.
    // Final cleanup (DELETE the row) is done by finalizePack() when
    // the player exits.
    if (!this._packCommitted) {
      this._packCommitted = true;
      if (this.props.onPackOpened) {
        // Fire-and-forget. The reveal animation should not wait on
        // the server round trip. If the open fails the parent will
        // surface a toast (see handlePackOpened in app.jsx).
        this.props.onPackOpened().catch(() => {});
      }
    }
    // The tear waits for the player and plays its own rip; the flat path
    // announces itself and runs on a timer.
    if (this.usesBurst()) {
      this.setState({ phase: 'opening' });
      // The scene has to become clickable on its own. Generous, because it is
      // fetching a 1.5MB model plus two textures off the local server.
      this.armSceneWatchdog(SCENE_READY_MS);
      return;
    }
    playSound('snd-pack-opening.mp3', 0.6);
    this.setState({ shaking: true, phase: 'opening' });
    setTimeout(this.enterSummary, 900);
  };

  // Delete the pending_packs row on exit. Cards were already granted
  // by handlePackClick; this is pure cleanup of the server-side
  // scaffolding. Safe to call more than once. The server is
  // idempotent on missing rows.
  //
  // Only finalize a pack the player actually opened. Backing out of the
  // picker before opening (e.g. opened the screen, changed their mind)
  // must leave the pack untouched. It was paid for and never granted.
  // The server enforces the same invariant (finalize ignores unopened
  // rows), but skipping the call here keeps intent explicit and avoids a
  // pointless round trip.
  finalizeAndExit = async (then) => {
    if (this._packCommitted && !this._packFinalized) {
      this._packFinalized = true;
      const packId = this.props.pack?.id;
      if (packId) {
        try { await finalizePack(packId); } catch (_) {}
      }
    }
    if (typeof then === 'function') then();
  };

  toggleRerollMode = (rarity) => {
    if (this._packFinalized || this.state.mutating) return;
    playUI(UI.HOVER, { volume: 0.4 });
    this.setState((s) => ({ rerollRarity: s.rerollRarity === rarity ? null : rarity }));
  };

  // Re-roll the card at `idx` for Foresight. The server prices and gates
  // it; on success the fan, the collection and the flags follow its answer.
  handleReroll = async (idx) => {
    const { mutating, packCards } = this.state;
    const packId = this.props.pack?.id;
    if (mutating || this._packFinalized || !packId) return;
    const entry = packCards[idx];
    const rarity = entry?.slotRarity || entry?.rarity;
    if (!rarity) return;
    this.setState({ mutating: true, mutationError: null });
    playUI(UI.CLICK);
    try {
      const result = await rerollPackCard(packId, idx);
      const [resolved] = resolvePackContents([result.newCard], this.props.sorceryCards || []);
      const nextCards = packCards.slice();
      if (resolved) nextCards[idx] = resolved;
      if (result.collection) this.props.onCollectionUpdate?.(result.collection);
      this.setState({
        packCards: nextCards,
        flags: { rerolledRarities: [], ...(result.pack?.flags || {}) },
        mutating: false,
        rerollRarity: null,
        flashIndex: idx,
      });
      playUI(UI.FOIL_REVEAL, { volume: 0.7 });
      setTimeout(() => { if (this.state.flashIndex === idx) this.setState({ flashIndex: -1 }); }, 700);
    } catch (err) {
      this.setState({ mutating: false, mutationError: err?.message || t('Re-roll failed'), rerollRarity: null });
    }
  };

  /** Turn a hidden card over, with the noise the moment deserves. */
  revealCard = (index, entry) => {
    if (this.state.revealed.has(index)) return;
    const next = new Set(this.state.revealed);
    next.add(index);
    this.setState({ revealed: next, flashIndex: index });
    const rarity = entry?.slotRarity || entry?.rarity;
    if (rarity === 'Unique' || rarity === 'Avatar') {
      playUI(UI.ACHIEVEMENT, { volume: 0.75 });
    } else {
      playUI(UI.FOIL_REVEAL, { volume: 0.85 });
    }
    // The flash is one-shot; clearing it lets a later card fire its own.
    setTimeout(() => {
      if (this.state.flashIndex === index) this.setState({ flashIndex: -1 });
    }, 700);
  };

  render() {
    const { pack, onDone, onOpenAnother, canAffordAnother, remainingPacks, continueLabel, backTitle } = this.props;
    const {
      phase, shaking, hoveredIndex, inspectedEntry, packCards,
      flags, rerollRarity, mutating, mutationError,
    } = this.state;
    // The Alchemist's potions that worked on this pack.
    const potionsApplied = [...new Set([
      ...(Array.isArray(flags.potions) ? flags.potions : []),
      ...Object.keys(flags.potionDetails || {}),
    ])];
    // Oracle Sight chips: one per rarity whose node is held and which this
    // pack has not re-rolled yet. Sealed-event packs have no server id and
    // stay untouched.
    const skillState = getSkillState();
    const foresight = this.props.profile?.oracle?.foresight || 0;
    const sightChips = pack?.id && phase === 'summary' && !this._packFinalized
      ? ['Ordinary', 'Exceptional', 'Elite', 'Unique']
        .map((r) => ({ rarity: r, cost: sightCostFor(skillState, r) }))
        .filter((c) => c.cost != null && !flags.rerolledRarities.includes(c.rarity))
      : [];
    const boosterImg = getBoosterImage(pack.setKey);
    const N = packCards.length;
    const packRows = Array.from({ length: Math.ceil(N / 8) }, (_, row) => packCards.slice(row * 8, (row + 1) * 8));
    // Three full-size rows fit above the fixed footer with tighter spacing.
    // Only larger sealed products need any scaling.
    const revealScale = Math.min(1, 3 / Math.max(1, packRows.length));
    const rowGap = packRows.length > 2 ? 0.625 : 1;
    // Post-open ownership. Cards were granted at handlePackClick, so
    // the player's collection already includes this pack's contents.
    const ownedMap = buildOwnedMap(this.props.profile?.collection || []);
    // Funnel every exit through finalizeAndExit which deletes the
    // now-redundant pending_packs row before navigating away.
    const handleExitDone = () => this.finalizeAndExit(onDone);
    const handleExitOpenAnother = () => this.finalizeAndExit(onOpenAnother);

    return (
      <div
        className="fixed inset-0 z-50 flex flex-col overflow-hidden"
        // The store's own backdrop, blurred and dimmed, carried all the way
        // through the opening. Flat black made the pack look cut out and
        // pasted onto nothing; keeping the room the player was just standing
        // in means the pack is opened *somewhere*. The scrim is part of the
        // same background stack rather than a separate element.
        //
        // Two layers darken, and the cost of the second one was easy to miss:
        // the flat scrim and the vignette compound, so the mid-field was
        // carrying both and sat far darker than either figure suggests. Both
        // came down together. The edges still ramp to solid black. That part
        // is load-bearing, not taste, and the note below says why.
        style={{ background: '#000' }}
      >
        {/* The room, lit the way the store lights it.

            A still of the store was never going to carry the atmosphere: the
            candle flames, the torch glow and the dust are live layers the
            store draws *over* its artwork, not pixels baked into it. Painting
            the image alone gave a room with every light out. These are the
            same three components the store mounts, in the same order, over the
            same artwork at the same aspect ratio. Which is what keeps the
            flames sitting on the painted candles, since their coordinates are
            image-relative. */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
          <CoverArtCanvas src="/store-bg-soft.webp" aspectRatio={STORE_ART_ASPECT} />

          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(rgba(6,5,8,0.26), rgba(6,5,8,0.38))' }}
          />
          {/* Falls to solid black at the edges. This overlay is `fixed`, but
              `.app-canvas` carries translateZ(0) and is therefore its
              containing block. So the backdrop stops at the canvas while the
              letterbox bars around it stay `#000`. Flat black used to match
              them exactly and the seam was invisible; a textured backdrop
              turned that boundary into a visible box around the whole screen.
              Vignetting to black hides the join wherever it happens to fall.
              `closest-side`, so the ramp reaches solid black at the middle of
              every edge. A default ellipse is sized to the farthest corner,
              which leaves the middle of the top and bottom edges short of black
              and the seam still faintly visible along them. */}
          <div
            className="absolute inset-0"
            style={{ background: 'radial-gradient(ellipse closest-side at center, rgba(6,5,8,0) 34%, rgba(6,5,8,0.24) 70%, rgba(0,0,0,1) 100%)' }}
          />

          <StoreTorchFX />

          {/* Candles go after the scrims so they sit on top of them, and in
              their own wrapper because CoverArtCanvas's transform makes a
              stacking context its children cannot escape. Both notes are the
              store's, and both still apply here. */}
          <CoverArtCanvas aspectRatio={STORE_ART_ASPECT}>
            <CandleFlames candles={STORE_DEFAULT_CANDLES} />
          </CoverArtCanvas>

          <AmbientParticles preset="store" />
        </div>

        {/* Particle FX overlay */}
        {/* The particle burst fires on the frame `active` first goes true, so
            for a pack that explodes it has to be held back until it does.
            otherwise the sparks go off while the pack is still sitting there
            sealed, waiting to be clicked. The flat path has no such moment and
            keeps firing as soon as the opening starts. */}
        {/* The particle canvas is only for the flat path. It is a full-screen
            surface repainted every frame under a 20px CSS blur; on the burst
            path the sparks are batched particles inside the WebGL scene
            instead, which is both cheaper and composites with the pack rather
            than floating over it. The lightning canvas is gone for the same
            reason. A third full-screen surface with per-stroke shadowBlur,
            layered on a scene that now carries its own light. */}
        {/* Outlives the phase change on purpose.
            Mounted inside the opening branch, the whole 3D layer unmounted the
            instant the reveal began. So the pack's pieces stopped in mid-air
            and vanished, and the cards appeared out of nothing. Kept here it
            goes on throwing debris behind the fan until its own sequence is
            spent. Full bleed, because the pack is framed by the camera and a
            box only gives the burst an edge to run into. */}
        {this.usesBurst() && !this.state.sceneFailed && !this.state.sceneSpent
          && (phase === 'opening' || phase === 'summary') ? (
            <div
              className="absolute inset-0"
              style={{
                zIndex: 2,
                // Clickable while it is the pack; inert once it is only
                // wreckage, or it would swallow clicks meant for the cards.
                pointerEvents: phase === 'opening' ? 'auto' : 'none',
              }}
            >
              <BoosterPackScene
                setKey={pack.setKey}
                hasFoil={packCards.some((e) => isFoilFinish(e.printing?.foiling))}
                // Loaded: the pack is on screen and clickable, and the wait is
                // now the player's own. Nothing to watch until they commit.
                onReady={this.clearSceneWatchdog}
                // Committed: charge → burst → cards is a fixed ~0.75s run on
                // the scene's own clock, so anything past this is a stall.
                onCharge={() => {
                  this.armSceneWatchdog(SCENE_BURST_MS);
                  this.setState({ charging: true });
                }}
                onBurst={() => this.setState({ burst: true })}
                onCardsOut={this.enterSummary}
                onDone={() => this.setState({ sceneSpent: true })}
                onUnavailable={this.handleSceneUnavailable}
              />
            </div>
          ) : null}

        <PackOpeningFX
          active={this.usesBurst() && !this.state.sceneFailed
            ? false
            : (phase === 'opening' || phase === 'summary')}
        />

        <div className="flex items-center gap-4 px-6 py-3 relative z-10" style={{ background: PANEL_BG, borderBottom: `1px solid ${GOLD} 0.15)`, backdropFilter: 'blur(8px)' }}>
          <BackButton title={backTitle || t('Back to Store')} onClick={handleExitDone} />
          <div className="text-sm font-semibold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{t('{set} Pack', { set: pack.setLabel })}</div>
          {remainingPacks > 0 ? <div className="text-xs" style={{ color: TEXT_MUTED }}>{t('{n} more to open', { n: remainingPacks })}</div> : null}
          {sightChips.map(({ rarity, cost }) => {
            const armed = rerollRarity === rarity;
            const affordable = foresight >= cost;
            return (
              <button
                key={`sight-${rarity}`}
                type="button"
                className="ml-2 px-3 py-1 text-xs font-semibold cursor-pointer"
                style={{ ...(armed ? GOLD_BTN : BEVELED_BTN), color: armed ? GOLD_BTN.color : TEXT_PRIMARY, opacity: affordable ? 1 : 0.45 }}
                disabled={mutating || !affordable}
                onClick={() => this.toggleRerollMode(rarity)}
                title={affordable ? t('Re-roll one {rarity} card for {n} Foresight', { rarity, n: cost }) : t('Not enough Foresight')}
              >
                {armed ? t('Pick a {rarity} card', { rarity }) : t('Re-roll {rarity} ({n} Foresight)', { rarity, n: cost })}
              </button>
            );
          })}
          {sightChips.length ? <div className="ml-2 text-xs tabular-nums" style={{ color: TEXT_MUTED }}>{t('Foresight {n}', { n: foresight })}</div> : null}
          {mutationError ? <div className="ml-2 text-xs" style={{ color: '#e89090' }}>{mutationError}</div> : null}
          {phase === 'summary' ? (
            <div className="ml-auto text-xs" style={{ color: TEXT_MUTED }}>{t('Space to inspect')}</div>
          ) : null}
        </div>

        {/* Transparent, but not inert: a div still catches clicks on its own
            box. While the pack is on screen this layer sits at z-10 over the
            3D canvas at z-2 and swallowed every click aimed at the pack. The
            pack could not be opened at all, while the server had already been
            told it was. Only the opening phase needs to fall through; the
            picker and the card fan both want their clicks. */}
        <div
          className="flex-1 min-h-0 flex flex-col items-center justify-center relative z-10"
          data-testid="pack-content"
          style={{ pointerEvents: phase === 'opening' ? 'none' : undefined }}
        >

          {phase === 'sealed' ? (
            <div className="absolute inset-x-0 top-0 z-20 pointer-events-none">
              <ActivePotions alchemist={this.props.profile?.alchemist} kind="pack" floating />
            </div>
          ) : null}

          {/* Sealed. Show all pending packs grouped by set */}
          {(phase === 'sealed' || phase === 'opening') ? (
            <div className="flex flex-col items-center gap-8">
              {phase === 'sealed' ? (
                <>
                  <h2 className="text-xl font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{t('Choose a Pack to Open')}</h2>
                  {/* Picker grid wraps to a new row every 5 items, and
                      the whole tray scrolls vertically when the player
                      has more unique products than fit in one viewport.
                      Without the wrap + scroll, a player who hoarded
                      packs from many sets pushes the rightmost tiles
                      off-screen with no way to reach them. */}
                  <div
                    // No `overflow-y-auto`, and no max-height to need it.
                    //
                    // That one class made this a scroll container, which
                    // Chromium promotes to its own compositing layer. And the
                    // layer was showing as a faintly lighter rectangle behind
                    // the packs, spanning exactly this element. It survived a
                    // vignette, a backdrop change and three attempts to
                    // reproduce it in isolation, because the cause is
                    // compositing rather than any painted style.
                    //
                    // The grid wraps at 92cqw and there are a handful of
                    // products, so it has no realistic way to overflow. If the
                    // catalogue ever grows enough to need scrolling, give the
                    // scroll to a wrapper that has nothing drawn near it rather
                    // than to the element sitting over the artwork.
                    // The gap went up when the tiles switched to the 3D
                    // renders: those are trimmed to the pack's own alpha, so
                    // unlike the flat catalogue art they carry no transparent
                    // margin of their own to space them apart.
                    className="flex flex-wrap items-end justify-center gap-x-20 gap-y-12 px-4 py-2"
                    style={{ maxWidth: '92cqw' }}
                  >
                    {(() => {
                      const allPending = [pack, ...(this.props.allPendingPacks || [])];
                      const bySet = {};
                      for (const p of allPending) {
                        bySet[p.setKey] = (bySet[p.setKey] || 0) + 1;
                      }
                      return Object.entries(bySet).map(([setKey, count]) => (
                        <motion.button
                          key={setKey}
                          type="button"
                          className="flex flex-col items-center gap-3 cursor-pointer"
                          whileHover={{ scale: 1.08 }}
                          whileTap={{ scale: 0.96 }}
                          onClick={() => {
                            if (this.props.onOpenFromSet) this.props.onOpenFromSet(setKey);
                          }}
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                        >
                          {hasProductPreview(setKey) ? (
                            <ProductPreview
                              setKey={setKey}
                              width="16cqw"
                              height="12cqw"
                            />
                          ) : (
                            <div style={{ transform: `scale(${getProductScale(setKey)})` }}>
                              <img
                                src={getProductImage(setKey)}
                                alt=""
                                className={cn(
                                  'object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.7)]',
                                  isSealedBoxKey(setKey)
                                    ? 'max-w-[16.25cqw] max-h-[16.25cqw]'
                                    : 'max-w-[11.25cqw] max-h-[17.5cqw]',
                                )}
                                draggable={false}
                              />
                            </div>
                          )}
                          <div className="text-sm font-semibold" style={{ color: TEXT_PRIMARY }}>{count}x</div>
                        </motion.button>
                      ));
                    })()}
                  </div>
                </>
              ) : (this.usesBurst() && !this.state.sceneFailed) ? (
                <>
                  <motion.p
                    className="text-sm absolute bottom-[6cqw] left-0 right-0 text-center pointer-events-none"
                    style={{ color: ACCENT_GOLD, visibility: this.state.charging ? 'hidden' : 'visible' }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ repeat: Infinity, duration: 1.6 }}
                  >
                    {t('Click the pack to open it')}
                  </motion.p>
                </>
              ) : (
                <motion.div className="flex flex-col items-center gap-6" initial={{ scale: 1 }} animate={{ scale: 1.1, opacity: 0.9 }} transition={{ duration: 0.3 }}>
                  <motion.div
                    animate={{ x: [0, -8, 8, -6, 6, -4, 4, -2, 2, 0], rotate: [0, -3, 3, -2.5, 2.5, -1.5, 1.5, -0.5, 0.5, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    {hasProductPreview(pack.setKey) ? (
                      <ProductPreview
                        setKey={pack.setKey}
                        width="25cqw"
                        height="18.75cqw"
                      />
                    ) : (
                      <div style={{ transform: `scale(${getProductScale(pack.setKey)})` }}>
                        <img
                          src={boosterImg}
                          alt=""
                          className={cn(
                            'object-contain drop-shadow-[0_25px_70px_rgba(0,0,0,0.8)]',
                            isSealedBoxKey(pack.setKey)
                              ? 'max-w-[21.25cqw] max-h-[21.25cqw]'
                              : 'max-w-[15cqw] max-h-[22.5cqw]',
                          )}
                          draggable={false}
                        />
                      </div>
                    )}
                  </motion.div>
                  <motion.p className="text-sm" style={{ color: ACCENT_GOLD }} animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 0.8 }}>{t('Opening...')}</motion.p>
                </motion.div>
              )}
            </div>
          ) : null}

          {/* Summary. At most eight cards per row, including bonus slots. */}
          {phase === 'summary' ? (
            <div className="absolute inset-0" data-testid="pack-summary">
              <div
                className={`absolute inset-x-0 top-0 bottom-[5.5cqw] flex flex-col items-center w-full px-8 ${packRows.length > 2 ? 'justify-start' : 'justify-center'}`}
                style={{ gap: `${rowGap}cqw`, paddingTop: packRows.length > 2 ? '0.5cqw' : 0 }}
                onMouseLeave={() => this.setState({ hoveredIndex: -1 })}
              >
                {packRows.map((row, rowIdx) => {
                  const burstReveal = this.usesBurst();
                  const rowStart = rowIdx * 8;
                  return (
                    <div key={rowIdx} data-testid="pack-card-row" className="relative flex items-end justify-center gap-2" style={{ perspective: '1200px' }}>
                      {row.map((entry, ri) => {
                        const i = rowStart + ri;
                        const rowN = row.length;
                        const rarity = entry.rarity || 'Ordinary';
                        // Display rides the distribution rarity, so AL
                        // Unique-Knights (printed Unique, pulled at the Elite
                        // rate) read as Elite in the fan instead of looking
                        // like they took a true-Unique slot.
                        const slotRarity = entry.slotRarity || rarity;
                        const isSite = entry.card?.type === 'Site' || entry.card?.played_horizontally;
                        const entryFoil = isFoilFinish(entry.printing?.foiling);
                        const isHovered = hoveredIndex === i;
                        // Held back until the player turns it over.
                        const hidden = burstReveal && startsFaceDown(entry)
                          && !this.state.revealed.has(i);
                        // Sight targeting: with a rarity armed, its cards
                        // ring up and the rest dim. A face-down card is never
                        // a target; turning it over first is the point.
                        const targeting = Boolean(rerollRarity);
                        const rerollTarget = targeting && !hidden && slotRarity === rerollRarity;
                        const isEntryDone = this.state.entryDone;
                        // Card dimensions in cqw. 170×237 design-px on the
                        // 1600 design canvas = 10.625cqw × 14.844cqw. The
                        // canvas-relative units mean the fan keeps the same
                        // visual proportions at every canvas size with zero
                        // JS scale math.
                        const cardWidth = `${(10.625 * revealScale).toFixed(3)}cqw`;
                        const cardHeight = `${(14.844 * revealScale).toFixed(3)}cqw`;

                        // Where this slot sits relative to the middle of the
                        // fan, so the card can start at the burst and fly out
                        // to here. Card box plus the row's gap-2 / gap-4,
                        // which resolve to 0.5cqw and 1cqw against the
                        // cqw-redefined --spacing token.
                        const slotW = 10.625 * revealScale + 0.5;
                        const slotH = 14.844 * revealScale + rowGap;
                        const entryOriginX = burstReveal
                          ? `${(-((ri - (rowN - 1) / 2) * slotW)).toFixed(2)}cqw`
                          : undefined;
                        const entryOriginY = burstReveal
                          ? `${(-((rowIdx - (packRows.length - 1) / 2) * slotH) - 2).toFixed(2)}cqw`
                          : undefined;

                        // Fan: rotation in degrees (scale-independent) and a
                        // small vertical dip on the edges expressed in cqw so
                        // it scales with the canvas. 12 design-px = 0.75cqw.
                        const t = rowN === 1 ? 0 : (ri / (rowN - 1)) - 0.5; // -0.5 to 0.5
                        const fanRotate = t * 6; // -3° to 3°
                        const fanY = `${Math.abs(t) * 0.75}cqw`;

                        // Foils get a one-time circular pivot once they've
                        // landed in the fan. The keyframes trace a closed
                        // orbit around the rest pose, so the card visibly
                        // tilts through every angle a player would normally
                        // catch by hand-wiggling. The foil overlay's
                        // gradients sweep in response to the rotation, so
                        // the prismatic shine actually animates instead of
                        // sitting still. Plays for ~1.7s, then settles.
                        return (
                          <PackRevealCard
                            key={i}
                            index={i}
                            entryInitialRotate={(Math.random() - 0.5) * 30}
                            entryOriginX={entryOriginX}
                            entryOriginY={entryOriginY}
                            faceDown={hidden}
                            cardBackUrl={burstReveal && startsFaceDown(entry) ? cardBackFor(entry) : undefined}
                            backRotated={isSite}
                            justRevealed={this.state.flashIndex === i}
                            animateOpacity={1}
                            animateY={fanY}
                            animateScale={1}
                            fanRotate={fanRotate}
                            zIndex={isHovered ? 100 : i + 1}
                            entryDone={isEntryDone}
                            entryFoil={entryFoil}
                            cardWidth={cardWidth}
                            cardHeight={cardHeight}
                            isHovered={isHovered}
                            auraClassName={entryFoil ? 'foil-card-aura' : undefined}
                            glowStyle={{
                              position: 'absolute',
                              inset: 0,
                              borderRadius: RADIUS.card,
                              // Rare tiers breathe. A static halo in a grid of
                              // fifteen tiles is wallpaper; a moving one is the
                              // thing you look at.
                              animation: !hidden && RARITY_BREATHES.has(slotRarity)
                                ? `vh-rarity-breathe ${slotRarity === 'Elite' ? 2.6 : 1.9}s ease-in-out infinite`
                                : undefined,
                              boxShadow: rerollTarget
                                ? '0 0 0 2px #82b4e6, 0 0 28px rgba(130,180,230,0.55)'
                                : entryFoil
                                  ? undefined
                                  : (isHovered ? RARITY_GLOW_HOVER[slotRarity] : RARITY_GLOW[slotRarity]),
                              transition: 'box-shadow 0.2s ease',
                              pointerEvents: 'none',
                            }}
                            cardBoxStyle={{
                              filter: targeting && !rerollTarget ? 'brightness(0.45) saturate(0.6)' : isHovered ? 'brightness(1.15)' : 'none',
                              cursor: 'pointer',
                              transition: 'filter 0.2s ease',
                            }}
                            tileEntry={{ card: entry.card, printing: entry.printing || {}, zone: 'spellbook', entryIndex: i }}
                            cornerBadge={isFreePlay() ? null : (
                              <CardOwnedBadge
                                owned={ownedMap.get(entry.card?.unique_id) || 0}
                                maxCopies={getMaxCopies(entry.card) || 4}
                                hideWhenZero
                              />
                            )}
                            onClick={() => {
                              if (hidden) {
                                this.revealCard(i, entry);
                                return;
                              }
                              if (targeting) {
                                if (rerollTarget) this.handleReroll(i);
                                return;
                              }
                              this.setState({ inspectedEntry: inspectedEntry === entry ? null : entry });
                            }}
                            onHoverChange={(hovered) => {
                              this.setState({ hoveredIndex: hovered ? i : -1 });
                              if (hovered) playUI(UI.HOVER, { volume: 0.4 });
                            }}
                            tooltip={{ name: entry.card?.name, rarityLabel: slotRarity, rarityColorClass: RARITY_LABEL_COLOR[slotRarity] }}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {potionsApplied.length > 0 ? (
                <div className="absolute left-[2cqw] bottom-[2cqw] z-20 flex flex-wrap items-center" style={{ width: '30cqw', gap: '0.375cqw 0.75cqw' }}>
                  {potionsApplied.map((k) => (
                    <div key={`potion-${k}`} className="flex items-center gap-1.5 text-xs" style={{ color: TEXT_PRIMARY }} data-testid={`potion-applied-${k}`}>
                      <img src={potionArt(k)} alt="" draggable={false} style={{ width: '1.25cqw', height: '1.25cqw', objectFit: 'contain', maskImage: potionArtMask(k), maskSize: '100% 100%' }} />
                      <span>{potionOutcome(k, flags.potionDetails?.[k])}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* Fixed footer: extra cards never move the action button. */}
              <motion.div
                className="absolute inset-x-0 bottom-0 flex justify-center gap-3 pb-8 z-10"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                {continueLabel ? (
                  // Caller (e.g. SealedPackOpener on the final pack)
                  // provides a custom forward button. Usually
                  // "Continue to Deck Builder." Fires onDone to stay
                  // consistent with the back/done route.
                  <button
                    type="button"
                    className="arena-heading px-7 py-3 text-base tracking-wide cursor-pointer pointer-events-auto"
                    style={FORGED_GOLD_STYLE_IDLE}
                    {...FORGED_GOLD_HANDLERS}
                    onClick={handleExitDone}
                  >
                    {continueLabel}
                  </button>
                ) : (
                  // Single CTA: "Open next pack" while packs remain,
                  // "Back to store" once the pool is exhausted. Both
                  // routes fire onOpenAnother so the parent decides
                  // the destination (arena → store; sealed → next pack
                  // in pool). Both labels stack in the same grid cell
                  // so the button reserves the wider label's width and
                  // never resizes when the text swaps.
                  <button
                    type="button"
                    className="arena-heading px-7 py-3 text-base tracking-wide cursor-pointer pointer-events-auto"
                    style={FORGED_GOLD_STYLE_IDLE}
                    {...FORGED_GOLD_HANDLERS}
                    onClick={handleExitOpenAnother}
                  >
                    <span className="grid grid-cols-1">
                      <span className="col-start-1 row-start-1" style={{ visibility: remainingPacks > 0 ? 'visible' : 'hidden' }}>{t('Open next pack')}</span>
                      <span className="col-start-1 row-start-1" style={{ visibility: remainingPacks > 0 ? 'hidden' : 'visible' }}>{t('Back to store')}</span>
                    </span>
                  </button>
                )}
              </motion.div>
            </div>
          ) : null}
        </div>

        {/* Card inspector */}
        {inspectedEntry ? (
          <CardInspector
            card={inspectedEntry.card}
            imageUrl={inspectedEntry.printing?.image_url}
            rarity={inspectedEntry.slotRarity || inspectedEntry.rarity}
            foiling={inspectedEntry.printing?.foiling}
            onClose={() => this.setState({ inspectedEntry: null })}
          />
        ) : null}
      </div>
    );
  }
}
